import { z } from 'zod'

/** Paste guardrail (not a chunk size): rows past this are dropped at parse. */
export const BATCH_ROW_CAP = 200

/** One pasted line — the member fields a single Invite Row carries. */
export type InviteRowFields = {
  firstName: string
  lastName: string
  email: string
}

export type ParseBatchPasteResult = {
  rows: InviteRowFields[]
  headerSkipped: boolean
  droppedOverCap: number
}

const emailSchema = z.email()

const HEADER_WORD = /first|last|name|e-?mail/i

function toCells(line: string): string[] {
  const cells = line.split('\t').map((cell) => cell.trim())
  // Excel pads trailing tabs; drop only trailing empty cells so a missing
  // middle field still surfaces as a validation error in the grid.
  while (cells.length > 0 && cells[cells.length - 1] === '') {
    cells.pop()
  }
  return cells
}

function isHeaderRow(cells: string[]): boolean {
  const emailCell = cells[2] ?? ''
  return !emailSchema.safeParse(emailCell).success && cells.some((cell) => HEADER_WORD.test(cell))
}

/** Parse TSV text exactly as copied from Excel into Invite Rows. */
export function parseBatchPaste(text: string): ParseBatchPasteResult {
  const lines = text.split(/\r\n|\n/).filter((line) => line.trim().length > 0)
  const cellRows = lines.map(toCells)

  const headerSkipped = cellRows.length > 0 && isHeaderRow(cellRows[0])
  const dataRows = headerSkipped ? cellRows.slice(1) : cellRows

  const kept = dataRows.slice(0, BATCH_ROW_CAP)
  return {
    rows: kept.map((cells) => ({
      firstName: cells[0] ?? '',
      lastName: cells[1] ?? '',
      email: cells[2] ?? '',
    })),
    headerSkipped,
    droppedOverCap: dataRows.length - kept.length,
  }
}
